import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import Mousetrap from '../../../../../shortcuts/Mousetrap';
import { openColorEyeDropper, isEyeDropperSupported } from '../../../../../../utils/color/eyedropperUtil';
import { hasNoModifierKeys } from '../../../../../../utils/keyboard/keyboardUtility';
import { getSelectedElementType } from '../colorToolUtils';
import { isPlatformPhoneOrMobileMode } from '../../../../../../platform/utils/platformDetailsUtils';
import globalLogger from '../../../../../../logger';
import { getColorToPersist } from '../../../../../../../common/colors/elementColorFormatUtil';

// Actions
import { updateSelectedElementsBackgroundColors, updateSelectedElementsColors } from '../colorActions';
import { updateLabel } from '../../../../../../../common/labels/labelActions';

// Selectors
import { activePopupSelector } from '../../../../../../components/popupPanel/popupOpenSelector';
import { getPlatformDetailsSelector } from '../../../../../../platform/platformSelector';
import { getActiveToolbarLabel } from '../../../../../../element/labels/labelSelectors';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { ElementType } from '../../../../../../../common/elements/elementTypes';

// Constants
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';

const logger = globalLogger.createChannel('workspace.toolbar');

const EYE_DROPPER_SHORTCUT = 'i';

type EyeDropperShortcutHandler = (event: KeyboardEvent) => void;

// Toolbar transitions briefly mount two colour tools, so the shortcut goes to the latest one still mounted
const eyeDropperShortcutHandlers: EyeDropperShortcutHandler[] = [];

const handleEyeDropperShortcutPress = (event: KeyboardEvent) =>
    eyeDropperShortcutHandlers[eyeDropperShortcutHandlers.length - 1]?.(event);

/**
 * Binds the eyedropper shortcut to the handler until the returned cleanup runs.
 */
const registerEyeDropperShortcut = (handler: EyeDropperShortcutHandler) => {
    eyeDropperShortcutHandlers.push(handler);
    Mousetrap.bind(EYE_DROPPER_SHORTCUT, handleEyeDropperShortcutPress);

    return () => {
        eyeDropperShortcutHandlers.splice(eyeDropperShortcutHandlers.indexOf(handler), 1);
        if (eyeDropperShortcutHandlers.length) return;

        Mousetrap.unbind(EYE_DROPPER_SHORTCUT);
    };
};

const CAPTION_SUPPORTED_ELEMENTS = [
    ElementType.IMAGE_TYPE,
    ElementType.FILE_TYPE,
    ElementType.SKETCH_TYPE,
    ElementType.LINK_TYPE,
    ElementType.DOCUMENT_TYPE,
    ElementType.TABLE_TYPE,
];

/**
 * Hook to handle eye dropper keyboard shortcut (i key) for color tools.
 * Bound through Mousetrap so it's ignored while typing in editable fields and while shortcuts are paused.
 *
 * @param selectedElements - List of selected elements
 * @param supportBackgroundColor - Whether element supports background color (false = foreground color)
 * @param isPopupOpen - Current popup state (undefined = always active)
 * @param shouldApplyBackgroundColor - Whether to apply background color
 */
const useColorToolEyeDropper = (
    selectedElements: Immutable.List<ImMNElement>,
    supportBackgroundColor = false,
    isPopupOpen?: boolean,
    shouldApplyBackgroundColor?: boolean,
) => {
    const dispatch = useDispatch();
    const activePopup = useSelector(activePopupSelector);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const activeToolbarLabel = useSelector(getActiveToolbarLabel);
    const isPhoneOrMobile = isPlatformPhoneOrMobileMode(platformDetails);

    useEffect(() => {
        // iOS keyboards type into an input that opts into Mousetrap shortcuts
        if (selectedElements?.size !== 1 || isPhoneOrMobile) {
            return;
        }

        // Check if any non-color popup is open, using the matching logic from the color tool
        // as it uses uuids in its popup ids
        const hasNonColorPopup = activePopup
            .keySeq()
            .some((popupId: string) => !popupId?.toLowerCase().startsWith(PopupIds.COLOR));

        // Don't trigger eyedropper for elements that support captions UNLESS the color popup is open, or we are updating a label instead of the element.
        // Typing on these starts their caption, but only while the element has focus, so Mousetrap alone can't tell.
        if (
            CAPTION_SUPPORTED_ELEMENTS.includes(getSelectedElementType(selectedElements)) &&
            !isPopupOpen &&
            !activeToolbarLabel
        ) {
            return;
        }

        // Legacy popups don't pause Mousetrap, so don't trigger if there's a non-color popup open
        if (hasNonColorPopup) {
            return;
        }

        const handleEyeDropperShortcut = async (event: KeyboardEvent) => {
            if (!hasNoModifierKeys(event)) return;

            event.preventDefault();

            if (!isEyeDropperSupported()) {
                logger.warn('EyeDropper API is not supported in this browser');
                return;
            }

            try {
                const color = await openColorEyeDropper();
                if (!color) {
                    return;
                }

                if (activeToolbarLabel) {
                    dispatch(updateLabel(activeToolbarLabel._id, { color: getColorToPersist(color) ?? undefined }));
                    return;
                }

                // Determine whether to apply background or foreground color
                const shouldApplyBackground = isPopupOpen ? shouldApplyBackgroundColor : supportBackgroundColor;

                if (shouldApplyBackground) {
                    dispatch(updateSelectedElementsBackgroundColors(color, null));
                } else {
                    dispatch(
                        updateSelectedElementsColors({
                            color,
                            useExistingColorSpace: false,
                            secondaryColor: null,
                        }),
                    );
                }
            } catch (error) {
                logger.error('Failed to use eyeDropper', error);
            }
        };

        return registerEyeDropperShortcut(handleEyeDropperShortcut);
    }, [
        selectedElements,
        supportBackgroundColor,
        isPopupOpen,
        shouldApplyBackgroundColor,
        isPhoneOrMobile,
        activePopup,
        activeToolbarLabel?._id,
    ]);
};

export default useColorToolEyeDropper;
